import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import useStickyHeaderState from '../../hooks/useStickyHeaderState';
import { CATEGORY_MFE_CONTEXT } from '../../tools/constants';
import GridCategories from '../GridCategories/GridCategories';

import HeaderFilters from '../ProductGridHeader/HeaderFilters';
import StoreToggle from '../StoreToggle';
import GridFilters from './GridFilters';
import styles from './GridFiltersAndCategories.module.scss';

const GridFiltersAndCategories = ({
  categoryId,
  facet = [],
  facetData = [],
  filter = '',
  gridHeaderDisplay,
  handleStoreFilter = () => { },
  hasShopMyStoreEnabled = false,
  onCheckBoxChange = () => { },
  onClearAllBtnClick = () => { },
  onFacetTagClick = () => { },
  onSortChange = () => { },
  onFacetToasterChange = () => { },
  resultsCount = null,
  selectedSort = '',
  sortData = { sortOptions: [] },
  storeDetails = { storeName: '', storeId: '' },
  currentCategoryId,
  parentCategoryId,
}) => {
  const isFacetSelected = facet?.length > 0 || !!filter;

  const { isNavbarVisible, isFiltersSticky } = useStickyHeaderState(CATEGORY_MFE_CONTEXT);
  const stickyTriggerRef = useRef(null);

  const [isStickyTriggerVisible, setIsStickyTriggerVisible] = useState(false);

  useEffect(() => {
    if (!stickyTriggerRef?.current) return () => { };

    const observer = new IntersectionObserver(
      ([entry]) => setIsStickyTriggerVisible(entry.isIntersecting),
      { rootMargin: '0px' },
    );

    observer.observe(stickyTriggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [stickyTriggerRef]);

  // Apply belowNavbar when sticky + navbar visible + breadcrumb not visible
  const belowNavbar = isFiltersSticky && isNavbarVisible && !isStickyTriggerVisible;

  const gridFilters = (
    <GridFilters
      categoryId={categoryId}
      facet={facet}
      facetData={facetData}
      filter={filter}
      handleStoreFilter={handleStoreFilter}
      hasShopMyStoreEnabled={hasShopMyStoreEnabled}
      onCheckBoxChange={onCheckBoxChange}
      onClearAllBtnClick={onClearAllBtnClick}
      onFacetTagClick={onFacetTagClick}
      onFacetToasterChange={onFacetToasterChange}
      onSortChange={onSortChange}
      resultsCount={resultsCount}
      selectedSort={selectedSort}
      sortData={sortData}
      storeDetails={storeDetails}
    />
  );

  const gridStoreToggle = hasShopMyStoreEnabled ? (
    <div className={styles.gridStoreToggle} data-testid="grid-store-toggle">
      <StoreToggle
        gridFilters
        handleStoreFilter={handleStoreFilter}
        isSearchPage={false}
        isShopMyStore={filter !== ''}
        storeDetails={storeDetails}
      />
    </div>
  ) : null;

  const appliedFilters = isFacetSelected ? (
    <div className={styles.appliedFilters} data-testid="applied-grid-filters">
      <HeaderFilters
        facet={facet}
        gridFilters
        isClearAllButtonEnabled={isFacetSelected}
        onClearAllBtnClick={onClearAllBtnClick}
        onFacetTagClick={onFacetTagClick}
      />
    </div>
  ) : null;

  return (
    <>
      <div ref={stickyTriggerRef} />
      <div
        className={classNames(
          styles.gridContainer,
          { [styles.belowNavbar]: belowNavbar },
        )}
      >
        <div className={styles.gridFiltersAndCategoriesContainer}>
          {gridHeaderDisplay === 'filtersAndCategories'
            ? (
              <div className={styles.gridFiltersAndCategories} data-testid="grid-filters-and-categories">
                <div className={styles.gridCategories} data-testid="grid-categories-wrapper">
                  <GridCategories
                    currentCategoryId={currentCategoryId}
                    parentCategoryId={parentCategoryId}
                  />
                </div>
                <div className={styles.gridFilters} data-testid="grid-filters">
                  {gridFilters}
                </div>
              </div>
            ) : (
              <div className={styles.gridFilters} data-testid="grid-filters">
                {gridFilters}
              </div>
            )}
          {gridStoreToggle}
        </div>
        {appliedFilters}
      </div>
    </>
  );
};

export default GridFiltersAndCategories;

GridFiltersAndCategories.propTypes = {
  gridHeaderDisplay: PropTypes.string.isRequired,
  currentCategoryId: PropTypes.string,
  parentCategoryId: PropTypes.string,
  categoryId: PropTypes.string,
  facet: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  facetData: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  filter: PropTypes.string,
  handleStoreFilter: PropTypes.func,
  hasShopMyStoreEnabled: PropTypes.bool,
  onCheckBoxChange: PropTypes.func,
  onClearAllBtnClick: PropTypes.func,
  onFacetTagClick: PropTypes.func,
  onSortChange: PropTypes.func,
  onFacetToasterChange: PropTypes.func,
  resultsCount: PropTypes.number,
  selectedSort: PropTypes.string,
  sortData: PropTypes.shape({
    sortOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
  }),
  storeDetails: PropTypes.shape({
    storeName: PropTypes.string,
    storeId: PropTypes.string,
  }),
};
